"use client";

import React from "react";
import { ArrowRight, CircleCheck as CheckCircle2, Globe, ShieldCheck, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const consultantPillars = [{
  icon: ShieldCheck,
  title: "International time-zone coverage",
  desc: "Flexible scheduling and proactive communication aligned with your regional working hours."
}, {
  icon: Globe,
  title: "International & remote",
  desc: "Advising UK, European, and global businesses across diverse search markets."
}, {
  icon: CheckCircle2,
  title: "Pragmatic SEO strategy",
  desc: "Clear roadmaps, technical clarity, and honest search measurement."
}];
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background text-foreground pt-12 pb-20 md:pt-16 md:pb-28 lg:pt-20 lg:pb-32 border-b border-border/60">
      {/* Decorative ambient background accents */}
      <div aria-hidden="true" className="pointer-events-none absolute -top-24 right-0 size-96 rounded-full bg-secondary/60 blur-3xl -z-10" />
      <div aria-hidden="true" className="pointer-events-none absolute top-1/2 -left-20 size-80 rounded-full bg-primary/5 blur-3xl -z-10" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Left Column: Core Value Proposition */}
          <div className="lg:col-span-7 flex flex-col items-start text-left">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="mb-5">
              <Badge variant="secondary" className="px-3 py-1.5 text-xs sm:text-sm font-medium border border-border/80 text-foreground bg-secondary/70 gap-2 rounded-full">
                <Sparkles className="size-3.5 text-primary" />
                <span>Independent Freelance SEO Consultant</span>
              </Badge>
            </motion.div>

            <motion.h1 initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.15]">
              Professional SEO Consultant for Sustainable Search Growth
            </motion.h1>

            <motion.p initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="mt-6 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              I partner directly with businesses across global markets, coordinating seamlessly
              across time zones from the UK and Europe to North America and the Middle East. When you hire me,
              you get direct communication tailored to your working hours—no account managers and no handoffs.
            </motion.p>

            {/* CTAs */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            y: 16
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
              <Button asChild size="lg" className="shadow-sm font-medium group">
                <Link to="/ContactPage">
                  <span>Book a Free SEO Consultation</span>
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="border-border text-foreground hover:bg-muted">
                <Link to="/CaseStudiesPage">
                  <span>Explore My Case Studies</span>
                </Link>
              </Button>
            </motion.div>

            {/* Direct Consultant Promise */}
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="mt-8 pt-6 border-t border-border w-full flex items-center gap-3 text-xs sm:text-sm text-muted-foreground">
              <span className="inline-flex items-center justify-center size-5 rounded-full bg-primary/10 text-primary shrink-0">
                <ShieldCheck className="size-3.5" />
              </span>
              <span>
                Personal accountability on every project. Available remotely for clients worldwide.
              </span>
            </motion.div>
          </div>

          {/* Right Column: Visual Portrait & Value Cards */}
          <div className="lg:col-span-5 flex flex-col items-center lg:items-end">
            <motion.div initial={shouldReduceMotion ? false : {
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative w-full max-w-md">
              {/* Photo Frame */}
              <div className="relative overflow-hidden rounded-2xl border border-border/80 bg-card shadow-lg p-2">
                <div className="relative aspect-4/5 overflow-hidden rounded-xl bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1762002/img/yassine_essaadi_seo_consultant.webp-wvc-sizes" alt="Yassine Essaadi, Freelance SEO Consultant" loading="eager" decoding="async" className="size-full object-cover object-top" />
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-foreground/80 via-foreground/20 to-transparent p-5 text-white">
                    <p className="font-serif text-lg font-semibold text-white">
                      Yassine Essaadi
                    </p>
                    <p className="text-xs text-white/90">
                      Independent SEO Strategy Consultant
                    </p>
                  </div>
                </div>
              </div>

              {/* Floating Badge */}
              <div className="mt-4 sm:absolute sm:-bottom-6 sm:-left-6 sm:mt-0 rounded-xl bg-background border border-border p-4 shadow-md sm:max-w-[240px]">
                <div className="flex items-center gap-2.5">
                  <div className="size-2.5 rounded-full bg-primary animate-pulse" />
                  <p className="text-xs font-semibold text-foreground">
                    Direct One-to-One Delivery
                  </p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground leading-relaxed">
                  Tailored international and national campaigns handled personally.
                </p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Bottom Feature Strip */}
        <div className="mt-16 sm:mt-20 pt-10 border-t border-border/80">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {consultantPillars.map((pillar, i) => {
            const PillarIcon = pillar.icon;
            return <div key={pillar.title} data-index={i} className="flex items-start gap-4 p-4 rounded-xl bg-card/60 border border-border/60 transition-[background-color,border-color] hover:bg-card hover:border-border">
                  <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0 mt-0.5">
                    <PillarIcon className="size-5" />
                  </div>
                  <div>
                    <h2 className="text-sm font-semibold text-foreground font-serif">
                      {pillar.title}
                    </h2>
                    <p className="mt-1 text-xs sm:text-sm text-muted-foreground leading-relaxed">
                      {pillar.desc}
                    </p>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}